import React, { useState } from "react";
import { Link } from "react-router";
import { SectionRule } from "components/common/SectionRule";
import { Spinner } from "components/common/Spinner";
import { TabToggle } from "components/common/TabToggle";
import { ExportAuditList } from "@/components/main/governance/ExportAuditList";
import { ExportAuditPanel } from "@/components/main/governance/ExportAuditPanel";
import { GovernanceAuditList } from "@/components/main/governance/GovernanceAuditList";
import { GovernanceAuditPanel } from "@/components/main/governance/GovernanceAuditPanel";
import { useExportAudit } from "@/components/main/governance/useExportAudit";
import { useGovernanceAudit } from "@/components/main/governance/useGovernanceAudit";
import type { Org } from "@/lib/api/organizations";

/// Rows in the inline preview; the panel pages through the rest.
const PREVIEW_ROWS = 5;

type Log = "changes" | "exports";

const LOGS = [
  { value: "changes", label: "Changes" },
  { value: "exports", label: "Exports" },
] as const;

/// Every governance act in the org — the space-scoped ones included, since an
/// admin wants one place to see them all rather than a tour of every space —
/// and every export of its tables. `admin` reads both as MyCorr's platform
/// admins do, names and reasons included, whatever the plan.
export const GovernancePanel: React.FC<{
  org: Pick<Org, "org_id" | "name"> & { role?: string };
  admin?: boolean;
}> = ({ org, admin = false }) => {
  const [log, setLog] = useState<Log>("changes");
  const [panelOpen, setPanelOpen] = useState<Log | null>(null);
  const audit = useGovernanceAudit(org.org_id, undefined, PREVIEW_ROWS, admin);
  const exports = useExportAudit(
    log === "exports" ? org.org_id : null,
    PREVIEW_ROWS,
    admin,
  );
  const shown = log === "changes" ? audit.entries : exports.entries;

  return (
    <>
      <section className="flex flex-col">
        <SectionRule
          label="Governance"
          action={
            shown.length > 0
              ? { label: "Show all", onClick: () => setPanelOpen(log) }
              : undefined
          }
        />
        <div className="flex flex-col gap-2">
          <TabToggle
            value={log}
            options={LOGS}
            onChange={setLog}
            ariaLabel="Governance log"
          />
          {log === "changes" ? (
            <>
              <span className="text-fg-muted-regular-small">
                Every organization, membership and space change, including those
                made by administrators.
              </span>
              {audit.available === null ? (
                <Spinner />
              ) : (
                <GovernanceAuditList
                  entries={audit.entries}
                  names={audit.names}
                  showSpace
                />
              )}
            </>
          ) : (
            <>
              <span className="text-fg-muted-regular-small">
                Every time someone took this organization’s table data out:
                downloads, clipboard copies and reads with an access token.
              </span>
              {exports.available === null ? (
                <Spinner />
              ) : exports.locked ? (
                <ExportsLocked org={org} />
              ) : (
                <ExportAuditList entries={exports.entries} />
              )}
            </>
          )}
        </div>
      </section>

      <GovernanceAuditPanel
        open={panelOpen === "changes"}
        onClose={() => setPanelOpen(null)}
        orgId={org.org_id}
        title={`Governance · ${org.name}`}
        showSpace
        admin={admin}
      />
      <ExportAuditPanel
        open={panelOpen === "exports"}
        onClose={() => setPanelOpen(null)}
        orgId={org.org_id}
        title={`Exports · ${org.name}`}
        admin={admin}
      />
    </>
  );
};

const ExportsLocked: React.FC<{ org: { org_id: string; role?: string } }> = ({
  org,
}) => (
  <span className="px-2 text-fg-muted-regular-small">
    The export log comes with a Pro plan.{" "}
    {org.role === "owner" ? (
      <Link
        to={`/organizations/${org.org_id}#plan`}
        className="text-accent hover:underline"
      >
        Upgrade to Pro
      </Link>
    ) : (
      "Ask an owner to upgrade."
    )}
  </span>
);
